import { supabase } from './supabase';

export type PhotoEntityType =
  | 'positive_observation'
  | 'safety_event'
  | 'swa_event'
  | 'jsa_evaluation';

export interface PhotoAttachmentRow {
  id: string;
  tenant_id: string;
  dsr_report_id: string;
  entity_type: PhotoEntityType;
  entity_id: string;
  storage_path: string;
  file_name: string;
  file_size: number | null;
  mime_type: string | null;
  caption: string | null;
  uploaded_by: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

// Why: bucket is PRIVATE (ARCH-015). All display paths go through createSignedUrl.
// 10MB matches the storage bucket policy — enforce client-side to fail fast.
export const MAX_FILE_SIZE = 10 * 1024 * 1024;
export const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
] as const;

const PHOTO_SELECT =
  'id, tenant_id, dsr_report_id, entity_type, entity_id, storage_path, file_name, file_size, mime_type, caption, uploaded_by, created_by, created_at, updated_at, is_deleted';

function extensionFor(mime: string): string {
  switch (mime) {
    case 'image/png':
      return 'png';
    case 'image/heic':
    case 'image/heif':
      return 'heic';
    case 'image/webp':
      return 'webp';
    default:
      return 'jpg';
  }
}

export interface UploadPhotoParams {
  file: File;
  tenantId: string;
  dsrId: string;
  entityType: PhotoEntityType;
  entityId: string;
  userId: string;
  caption?: string;
}

// Why: storage path must match the mobile pattern exactly so photos uploaded
// on web appear on mobile and vice versa — see mozaic-mobile/src/services/dsr.ts
// uploadPhoto. Path is {tenant_id}/{dsr_id}/{entity_type}/{entity_id}/{uuid}.{ext}.
export async function uploadPhoto(params: UploadPhotoParams): Promise<PhotoAttachmentRow> {
  const { file, tenantId, dsrId, entityType, entityId, userId, caption } = params;

  if (file.size > MAX_FILE_SIZE) {
    throw new Error('File too large (max 10MB)');
  }
  if (!(ALLOWED_MIME_TYPES as readonly string[]).includes(file.type)) {
    throw new Error('Invalid file type. Use JPG, PNG, HEIC, or WebP.');
  }

  const photoId = window.crypto.randomUUID();
  const ext = extensionFor(file.type);
  const storagePath = `${tenantId}/${dsrId}/${entityType}/${entityId}/${photoId}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('dsr-photos')
    .upload(storagePath, file, {
      contentType: file.type,
      upsert: false,
      cacheControl: '3600',
    });
  if (uploadError) throw new Error(uploadError.message);

  const { data, error } = await supabase
    .from('photo_attachments')
    .insert({
      tenant_id: tenantId,
      dsr_report_id: dsrId,
      entity_type: entityType,
      entity_id: entityId,
      storage_path: storagePath,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type,
      caption: caption ?? null,
      uploaded_by: userId,
      created_by: userId,
    })
    .select(PHOTO_SELECT)
    .single();

  if (error) {
    // Cleanup: remove the uploaded file to avoid orphaned storage objects
    // when the DB insert fails (e.g. RLS rejection).
    await supabase.storage.from('dsr-photos').remove([storagePath]).catch(() => {});
    throw new Error(error.message);
  }

  return data as PhotoAttachmentRow;
}

export async function deletePhoto(photoId: string): Promise<void> {
  // Soft delete only — storage file is retained for audit trail.
  const { error } = await supabase
    .from('photo_attachments')
    .update({ is_deleted: true })
    .eq('id', photoId);
  if (error) throw new Error(error.message);
}

export async function getSignedUrl(
  storagePath: string,
  expirySeconds: number = 300,
): Promise<string | null> {
  try {
    const { data, error } = await supabase.storage
      .from('dsr-photos')
      .createSignedUrl(storagePath, expirySeconds);
    if (error || !data) return null;
    return data.signedUrl;
  } catch {
    return null;
  }
}

export async function getPhotosForEntity(
  entityType: PhotoEntityType,
  entityId: string,
): Promise<PhotoAttachmentRow[]> {
  const { data, error } = await supabase
    .from('photo_attachments')
    .select(PHOTO_SELECT)
    .eq('entity_type', entityType)
    .eq('entity_id', entityId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as PhotoAttachmentRow[];
}
